import { setUser } from '@sentry/vue'
import type { TOURS_IDS } from '~/constants'
import type { PaginatedResponse, User, UsersFilters, ApiResponse, InvitedUserResponse, SetPasswordDto } from '~/types'

export const useGetUsers = (params: UsersFilters) => {
  const { $api } = useNuxtApp()

  return $api<PaginatedResponse<User[]>>('users', {
    method: 'GET',
    params,
  })
}

export type CreateUserDTO = Pick<User, 'name' | 'email'> & { role_id?: number }

export const useCreateUser = (body: CreateUserDTO) => {
  const { $api } = useNuxtApp()

  return $api<PaginatedResponse<User>>('users', {
    method: 'POST',
    body,
  })
}

export const useInviteUser = (id: User['id']) => {
  const { $api } = useNuxtApp()

  return $api<PaginatedResponse<User>>(`users/${id}/invitations`, {
    method: 'POST',
  })
}

export const useInactivateUser = (id: User['id']) => {
  const { $api } = useNuxtApp()

  return $api<PaginatedResponse<User>>(`users/${id}/inactive`, {
    method: 'PATCH',
  })
}

export const useGetUser = async () => {
  const { $api } = useNuxtApp()
  const authStore = useAuthStore()

  const response = await $api<PaginatedResponse<User>>(`users/me`, {
    method: 'GET',
  })

  setUser({
    id: response.data.id!,
    username: response.data.name,
    email: response.data.email,
    role: response.data.role.name,
  })

  authStore.setUser(response.data)

  return response
}

export const useUpdateTourStatus = (tourId: TOURS_IDS) => {
  const { $api } = useNuxtApp()

  return $api<ApiResponse>(`users/tours/${tourId}`, {
    method: 'POST',
  })
}

export const useGetInvitedUserByToken = (token: string) => {
  const { $api } = useNuxtApp()

  return $api<ApiResponse<InvitedUserResponse>>(`users/invited-user-by-token/${token}`, {
    method: 'GET',
  })
}

export const useSetPasswordAfterInvite = (body: SetPasswordDto, tempToken: string) => {
  const { $api } = useNuxtApp()

  return $api<ApiResponse<InvitedUserResponse>>(`users/invite/set-password`, {
    method: 'POST',
    body,
    headers: {
      Authorization: `Bearer ${tempToken}`,
    },
  })
}
